import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';
import {
  FLAG_GETTING_MAINTENANCE_SERVICE_ORDERS,
  GET_MAINTENANCE_SERVICE_ORDERS,
  CLEAR_MAINTENANCE_SERVICE_ORDERS,
  FLAG_MAINTENANCE_SERVICE_ORDER_ACTIVITY,
  GET_MAINTENANCE_SERVICE_ORDER,
  CLEAR_MAINTENANCE_SERVICE_ORDER,
  GET_MAINTENANCE_SERVICE_ORDER_DAILY_ACTIVITIES,
  CLEAR_MAINTENANCE_SERVICE_ORDER_DAILY_ACTIVITIES,
} from '../types/mechanical-maintenance';
import {
  generateChangeMaintenanceServiceOrderStatusEndpoint,
  generatePutMaintenanceServiceOrderEndpoint,
  MAINTENANCE_SERVICE_ORDER_ENDPOINT,
  MY_MAINTENANCE_SERVICE_ORDERS_ENDPOINT,
} from '../../config/endpoints';
import {
  DEFAULT_GET_CONFIG,
  DEFAULT_POST_CONFIG,
  DEFAULT_DELETE_CONFIG,
  DEFAULT_PUT_CONFIG,
} from '../../config/rest';
import { isErrorResponse } from '../../utils/error-handlers';
import {
  MAINTENANCE_SERVICE_ORDER_PATH,
  generateEditMaintenanceServiceOrderStep2,
} from '../../config/paths';
import { tzNormalizeDate } from '../../utils/date';

const flagGettingMaintenanceServiceOrders = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_MAINTENANCE_SERVICE_ORDERS,
    payload: flag,
  });

const getMaintenanceServiceOrders =
  async (tableFilters) => async (dispatch) => {
    try {
      dispatch(flagGettingMaintenanceServiceOrders(true));
      const url = `${MAINTENANCE_SERVICE_ORDER_ENDPOINT}?${QueryString.stringify(
        tableFilters,
      )}`;
      const promise = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(promise, null, dispatch);
      const response = await promise.json();
      dispatch({
        type: GET_MAINTENANCE_SERVICE_ORDERS,
        payload: response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagGettingMaintenanceServiceOrders(false));
    }
  };

const clearMaintenanceServiceOrders = () => (dispatch) =>
  dispatch({
    type: CLEAR_MAINTENANCE_SERVICE_ORDERS,
  });

const flagMaintenanceServiceOrderActivity = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_MAINTENANCE_SERVICE_ORDER_ACTIVITY,
    payload: flag,
  });

const getMaintenanceServiceOrder =
  async ({ maintenanceServiceOrderId }) =>
  async (dispatch) => {
    try {
      dispatch(flagMaintenanceServiceOrderActivity(true));
      const url = `${MAINTENANCE_SERVICE_ORDER_ENDPOINT}/${maintenanceServiceOrderId}`;
      const promise = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(promise, null, dispatch);
      const response = await promise.json();
      dispatch({
        type: GET_MAINTENANCE_SERVICE_ORDER,
        payload: response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagMaintenanceServiceOrderActivity(false));
    }
  };

const clearMaintenanceServiceOrder = () => (dispatch) =>
  dispatch({
    type: CLEAR_MAINTENANCE_SERVICE_ORDER,
  });

const postMaintenanceServiceOrder =
  async ({
    maintenanceWarningList,
    registeredBusId,
    deadline,
    priority,
    description,
    status,
    selectedMaintenancePlanningList,
    materialAssigmentList,
  }) =>
  async (dispatch) => {
    try {
      dispatch(flagMaintenanceServiceOrderActivity(true));
      const payload = {
        maintenanceWarningList,
        registeredBusId,
        deadline,
        priority,
        description,
        status,
        selectedMaintenancePlanningList,
        materialAssigmentList,
      };
      const promise = await fetch(MAINTENANCE_SERVICE_ORDER_ENDPOINT, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });
      await isErrorResponse(promise, null, dispatch);
      const response = await promise.json();

      dispatch(push(`${MAINTENANCE_SERVICE_ORDER_PATH}/${response.id}`));
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagMaintenanceServiceOrderActivity(false));
    }
  };

const putMaintenanceServiceOrder =
  async (
    maintenanceServiceOrderId,
    {
      maintenanceWarningList,
      registeredBusId,
      deadline,
      priority,
      description,
      status,
    },
    insideSteps = false,
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagMaintenanceServiceOrderActivity(true));
      const payload = {
        id: maintenanceServiceOrderId,
        maintenanceWarningList,
        registeredBusId,
        deadline,
        priority,
        description,
        status,
        lastUpdate: tzNormalizeDate(),
      };
      const url = generatePutMaintenanceServiceOrderEndpoint(
        maintenanceServiceOrderId,
      );
      const promise = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });
      await isErrorResponse(promise, null, dispatch);
      await promise.json();
      if (!insideSteps) dispatch(push(MAINTENANCE_SERVICE_ORDER_PATH));
      else
        dispatch(
          push(
            generateEditMaintenanceServiceOrderStep2(maintenanceServiceOrderId),
          ),
        );
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagMaintenanceServiceOrderActivity(false));
    }
  };

const getMyMaintenanceServiceOrders =
  async (tableFilters) => async (dispatch) => {
    try {
      dispatch(flagGettingMaintenanceServiceOrders(true));
      const url = `${MY_MAINTENANCE_SERVICE_ORDERS_ENDPOINT}?${QueryString.stringify(
        tableFilters,
      )}`;
      const promise = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(promise, null, dispatch);
      const response = await promise.json();
      dispatch({
        type: GET_MAINTENANCE_SERVICE_ORDERS,
        payload: response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagGettingMaintenanceServiceOrders(false));
    }
  };

const deleteMaintenanceServiceOrder =
  async ({ maintenanceServiceOrderId }) =>
  async (dispatch) => {
    try {
      dispatch(flagMaintenanceServiceOrderActivity(true));
      const url = `${MAINTENANCE_SERVICE_ORDER_ENDPOINT}/${maintenanceServiceOrderId}`;
      const promise = await fetch(url, {
        ...DEFAULT_DELETE_CONFIG,
      });
      await isErrorResponse(promise, null, dispatch);
      await promise.json();
      dispatch(push(MAINTENANCE_SERVICE_ORDER_PATH));
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagMaintenanceServiceOrderActivity(false));
    }
  };

const getMyMaintenanceServiceOrderDailyActivities =
  ({ registeredBusId, date }) =>
  async (dispatch) => {
    try {
      dispatch(flagMaintenanceServiceOrderActivity(true));

      const queryParams = QueryString.stringify({
        registeredBusId,
        date,
      });

      const url = `${MAINTENANCE_SERVICE_ORDER_ENDPOINT}/activities?${queryParams}`;

      const promise = await fetch(url, DEFAULT_GET_CONFIG);

      await isErrorResponse(promise, null, dispatch);

      const response = await promise.json();

      dispatch({
        type: GET_MAINTENANCE_SERVICE_ORDER_DAILY_ACTIVITIES,
        payload: response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagMaintenanceServiceOrderActivity(false));
    }
  };

const clearMaintenanceServiceOrderDailyActivities = () => (dispatch) =>
  dispatch({
    type: CLEAR_MAINTENANCE_SERVICE_ORDER_DAILY_ACTIVITIES,
  });

const fetchMaintenanceServiceOrderDetail = async (
  maintenanceServiceOrderId,
  dispatch,
) => {
  const url = `${MAINTENANCE_SERVICE_ORDER_ENDPOINT}/${maintenanceServiceOrderId}`;

  const response = await fetch(url, DEFAULT_GET_CONFIG);

  await isErrorResponse(response, null, dispatch);

  const serviceOrder = await response.json();

  dispatch({
    type: GET_MAINTENANCE_SERVICE_ORDER,
    payload: serviceOrder,
  });

  return serviceOrder;
};

const changeMaintenanceServiceOrderStatus =
  ({ maintenanceServiceOrderId, status, refreshServiceOrder = false }) =>
  async (dispatch) => {
    try {
      dispatch(flagMaintenanceServiceOrderActivity(true));

      const url = generateChangeMaintenanceServiceOrderStatusEndpoint(
        maintenanceServiceOrderId,
        status,
      );

      const response = await fetch(url, DEFAULT_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const updatedServiceOrder = await response.json();

      if (refreshServiceOrder) {
        await fetchMaintenanceServiceOrderDetail(
          maintenanceServiceOrderId,
          dispatch,
        );
      }

      toastr.success(
        'Orden de servicio',
        'El estado se actualizó correctamente.',
      );

      return updatedServiceOrder;
    } catch (error) {
      toastr.error('Error', error.message);
      return null;
    } finally {
      dispatch(flagMaintenanceServiceOrderActivity(false));
    }
  };

export {
  flagGettingMaintenanceServiceOrders,
  getMaintenanceServiceOrders,
  clearMaintenanceServiceOrders,
  flagMaintenanceServiceOrderActivity,
  getMaintenanceServiceOrder,
  postMaintenanceServiceOrder,
  clearMaintenanceServiceOrder,
  deleteMaintenanceServiceOrder,
  putMaintenanceServiceOrder,
  getMyMaintenanceServiceOrders,
  getMyMaintenanceServiceOrderDailyActivities,
  clearMaintenanceServiceOrderDailyActivities,
  changeMaintenanceServiceOrderStatus,
};
